/**
 * Utilidades de formato de fecha multi-tenant.
 * Cada empresa (tenant) tiene su propia zona horaria configurada en el backend.
 * NO hardcodear America/Bogota aquí — leemos del contexto global de empresa.
 */

const LS_KEY_TIMEZONE = 'empresa_timezone';

/**
 * Guarda la zona horaria de la empresa en localStorage (llamar al login o al cargar).
 */
export function setEmpresaTimezone(tz) {
  if (tz && typeof tz === 'string') {
    localStorage.setItem(LS_KEY_TIMEZONE, tz);
  }
}

/**
 * Retorna la zona horaria del tenant actual. Si no se ha cargado, retorna 'UTC' como neutral.
 */
export function getEmpresaTimezone() {
  return localStorage.getItem(LS_KEY_TIMEZONE) || 'UTC';
}

/**
 * Asegura que un string UTC venga con sufijo 'Z' para que el navegador lo interprete correctamente.
 */
function ensureUtcString(s) {
  if (!s) return null;
  // Ya tiene Z o offset explícito
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return s;
  return s + 'Z';
}

/**
 * Formatea un datetime UTC como fecha+hora en la zona horaria de la empresa.
 * Ejemplo: "23/04/2026, 17:30"
 */
export function fmtFechaHora(utcStr) {
  if (!utcStr) return '—';
  const tz = getEmpresaTimezone();
  try {
    const d = new Date(ensureUtcString(utcStr));
    if (isNaN(d.getTime())) return utcStr;
    return d.toLocaleString('es-CO', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (e) {
    return utcStr;
  }
}

/**
 * Formatea un datetime UTC como solo fecha en la zona horaria de la empresa.
 * Ejemplo: "23/04/2026"
 */
export function fmtFecha(utcStr) {
  if (!utcStr) return '—';
  const tz = getEmpresaTimezone();
  try {
    const d = new Date(ensureUtcString(utcStr));
    if (isNaN(d.getTime())) return utcStr;
    return d.toLocaleDateString('es-CO', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (e) {
    return utcStr;
  }
}

/**
 * Formatea solo la hora (HH:MM) en la zona horaria de la empresa.
 */
export function fmtHora(utcStr) {
  if (!utcStr) return '—';
  const tz = getEmpresaTimezone();
  try {
    const d = new Date(ensureUtcString(utcStr));
    if (isNaN(d.getTime())) return utcStr;
    return d.toLocaleTimeString('es-CO', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (e) {
    return utcStr;
  }
}
